import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { getCategories, getProductsByCategory, API_BASE_URL } from '../API/endpoint';
import Product from '../components/Product';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import './CategoryProducts.css';

function CategoryProducts() {
  const { category } = useParams();
  const categoryName = category.replace(/-/g, ' ');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
    loadData();
  }, [category]);

  useEffect(() => {
    loadData();
  }, [currentPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      const categoriesData = await getCategories();
      setCategories(categoriesData);
      
      const foundCategory = categoriesData.find(cat => 
        cat.name.toLowerCase().replace(/\s+/g, '-') === category
      );
      
      if (foundCategory) {
        setCurrentCategory(foundCategory);
        try {
          const response = await getProductsByCategory(foundCategory.id, currentPage, 20);
          
          if (response && typeof response === 'object' && Array.isArray(response.products)) {
            setProducts(response.products.sort((a, b) => b.id - a.id));
            setTotalPages(response.pagination ? response.pagination.total_pages : 1);
          } else {
            console.error('Unexpected response format:', response);
            setProducts([]);
            setTotalPages(1);
            setError('Format de réponse inattendu du serveur');
          }
        } catch (apiError) {
          console.error('API Error:', apiError);
          setError('Erreur lors du chargement des produits');
          setProducts([]);
          setTotalPages(1);
        }
      } else {
        setError('Catégorie introuvable');
      }
      
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderSkeletonCards = () => {
    return Array.from({ length: 20 }, (_, index) => (
      <div key={index} className="category-product-card skeleton-card">
        <Skeleton height={200} baseColor="#f0f0f0" highlightColor="#e0e0e0" />
        <Skeleton height={20} width="80%" baseColor="#f0f0f0" highlightColor="#e0e0e0" style={{ margin: '10px 0' }} />
        <Skeleton height={15} width="100%" count={2} baseColor="#f0f0f0" highlightColor="#e0e0e0" style={{ margin: '5px 0' }} />
        <Skeleton height={20} width="50%" baseColor="#f0f0f0" highlightColor="#e0e0e0" />
      </div>
    ));
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  return (
    <main className="App-main">
      <Helmet>
        <title>{categoryName} - Déco Ariège</title>
        <meta name="description" content={`Découvrez nos produits en bois artisanal de la catégorie ${categoryName}. Objets de décoration faits main en Ariège.`} />
      </Helmet>
      <div className="category-container magicpatern4">
        <div className="category-left-section">
          <div className="category-products-content">
            <h1>Produits - {categoryName}</h1>
            <Link to="/boutique" className="category-back-link">← Retour à la Boutique</Link>
            <div className="category-other-categories">
              <h3>Autres catégories :</h3>
              <div className="category-other-categories-list">
                {categories.map((cat) => (
                  <Link key={cat.id} to={`/boutique/${cat.name.toLowerCase().replace(/\s+/g, '-')}`} className={`category-other-category-link ${cat.id === currentCategory?.id ? 'active' : ''}`}>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
            {error ? (
              <p style={{color: 'red'}}>{error}</p>
            ) : loading ? (
              <div className="category-products-grid">
                {renderSkeletonCards()}
              </div>
            ) : (
              <div className="category-products-grid">
                {products.length === 0 ? (
                  <p>Aucun produit disponible dans cette catégorie.</p>
                ) : (
                  products.map((product) => (
                    <div key={product.id} className="category-product-card" onClick={() => handleProductClick(product)}>
                      {(product.thumbnailPath || product.path) && <img src={`${API_BASE_URL}${product.thumbnailPath || product.path}`} alt={product.title} />}
                      <div className='product-text'>
                        <h3>{product.title}</h3>  
                        <p className="category-product-price">{parseFloat(product.price).toFixed(2)}€</p>                      
                      </div>
                      <p className="category-product-description">{product.description || 'Réalisé à la main.'}</p>
                    </div>
                  ))
                )}
              </div>
            )}
            {!loading && !error && totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Précédent
                </button>
                <span className="pagination-info">
                  Page {currentPage} sur {totalPages}
                </span>
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        </div>
        {selectedProduct && (
          <div className="category-right-section">
            <Product product={selectedProduct} onClose={handleCloseModal} />
          </div>
        )}
      </div>
    </main>
  );
}

export default CategoryProducts;